/**
 * Pure coordinate-transformation helpers for the 2D building map.
 *
 * `AffineImage` defines a transform from pixel coordinates in the floorplan
 * PNG to world coordinates: world = R(yaw) * scale * pixel + offset. We
 * invert this for click-to-world hit testing on canvas-relative mouse
 * events.
 */

import type { components } from '../../api/generated/openapi';
type AffineImage = components['schemas']['AffineImage'];

/**
 * Convert a pixel-space coordinate (in the floorplan image) to a world-space
 * coordinate using the affine transform encoded by the image.
 */
export function pixelToWorld(
  px: number,
  py: number,
  image: AffineImage,
): { x: number; y: number } {
  const cosY = Math.cos(image.yaw);
  const sinY = Math.sin(image.yaw);
  return {
    x: image.x_offset + px * image.scale * cosY - py * image.scale * sinY,
    y: image.y_offset + px * image.scale * sinY + py * image.scale * cosY,
  };
}

/**
 * Convert a world-space coordinate back to pixel-space (inverse of
 * {@link pixelToWorld}). Used by hit testing when an image is the active
 * layer.
 */
export function worldToPixel(
  wx: number,
  wy: number,
  image: AffineImage,
): { x: number; y: number } {
  const cosY = Math.cos(image.yaw);
  const sinY = Math.sin(image.yaw);
  const dx = wx - image.x_offset;
  const dy = wy - image.y_offset;
  const s = image.scale || 1;
  return {
    x: (dx * cosY + dy * sinY) / s,
    y: (-dx * sinY + dy * cosY) / s,
  };
}

/**
 * Distance from a point to a line segment (v1-v2) in world coordinates.
 * Returns the squared distance (cheaper; compare against squared radii).
 */
export function pointToSegmentDistanceSq(
  px: number,
  py: number,
  v1x: number,
  v1y: number,
  v2x: number,
  v2y: number,
): number {
  const dx = v2x - v1x;
  const dy = v2y - v1y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = px - v1x;
    const ey = py - v1y;
    return ex * ex + ey * ey;
  }
  let t = ((px - v1x) * dx + (py - v1y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projx = v1x + t * dx;
  const projy = v1y + t * dy;
  const ex = px - projx;
  const ey = py - projy;
  return ex * ex + ey * ey;
}

/**
 * Distance from a point to a circle (robot marker) in world coordinates.
 */
export function pointInCircle(
  px: number,
  py: number,
  cx: number,
  cy: number,
  r: number,
): boolean {
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy <= r * r;
}

/**
 * World-space bounding box for a level: computed from its places, doors,
 * lifts, and nav graph nodes. The map renderer uses this for the
 * "fit-to-building" button.
 */
export interface WorldBBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function expandBBox(bb: WorldBBox, x: number, y: number): WorldBBox {
  return {
    minX: Math.min(bb.minX, x),
    minY: Math.min(bb.minY, y),
    maxX: Math.max(bb.maxX, x),
    maxY: Math.max(bb.maxY, y),
  };
}

export function emptyBBox(): WorldBBox {
  return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}

export function bboxIsEmpty(bb: WorldBBox): boolean {
  return !Number.isFinite(bb.minX) || !Number.isFinite(bb.minY);
}